import { useRef, useState } from 'react';
import styles from './Dashboard.module.scss';
import { DashboardItem, DashboardItemProps } from './components/DashboardItem/DashboardItem';
import { DashboardMobileItem } from './components/DashboardMobileItem/DashboardMobileItem';
import { FilterList } from './components/FilterList/FilterList';
import { FilterChips, FiltersListModal, Icon, Text } from 'components';
import { useDevice, useOutsideClick } from 'hooks';
import { selectAppModals } from 'store/app/selectors';
import { appActions } from 'store/app/slice';
import { selectCurrentUser } from 'store/auth/selectors';
import {
  selectDashboardFilter,
  selectDashboardFinishedCases,
  selectDashboardFinishedCasesFilter,
  selectDashboardFinishedCasesInfo,
  selectDashboardInTreatmentCases,
  selectDashboardInTreatmentCasesFilter,
  selectDashboardInTreatmentCasesInfo,
  selectDashboardLetterCases,
  selectDashboardLetterCasesFilter,
  selectDashboardLetterCasesInfo,
  selectDashboardNewCases,
  selectDashboardNewCasesFilter,
  selectDashboardNewCasesInfo,
  selectDashboardOutOfTreatmentCases,
  selectDashboardOutOfTreatmentCasesFilter,
  selectDashboardOutOfTreatmentCasesInfo,
} from 'store/dashboard/selectors';
import { dashboardActions } from 'store/dashboard/slice';
import { useDispatch, useSelector } from 'store/utils';

const blocks: Omit<DashboardItemProps, 'activeTab' | 'setActiveTab' | 'currentUser'>[] = [
  {
    name: 'New patients',
    iconName: 'DiagnosisLarge',
    thunk: dashboardActions.getPatientCasesDashboardNew,
    selectCases: selectDashboardNewCases,
    selectInfo: selectDashboardNewCasesInfo,
    selectFilter: selectDashboardNewCasesFilter,
    setFilter: dashboardActions.setNewCasesFilter,
  },
  {
    name: 'Letter review',
    iconName: 'Letter',
    thunk: dashboardActions.getPatientCasesDashboardLetter,
    selectCases: selectDashboardLetterCases,
    selectInfo: selectDashboardLetterCasesInfo,
    selectFilter: selectDashboardLetterCasesFilter,
    setFilter: dashboardActions.setLetterCasesFilter,
  },
  {
    name: 'In treatment',
    iconName: 'inTreatment',
    thunk: dashboardActions.getPatientCasesDashboardInTreatment,
    selectCases: selectDashboardInTreatmentCases,
    selectInfo: selectDashboardInTreatmentCasesInfo,
    selectFilter: selectDashboardInTreatmentCasesFilter,
    setFilter: dashboardActions.setInTreatmentCasesFilter,
  },
  {
    name: 'Finished',
    iconName: 'Finished',
    thunk: dashboardActions.getPatientCasesDashboardFinished,
    selectCases: selectDashboardFinishedCases,
    selectInfo: selectDashboardFinishedCasesInfo,
    selectFilter: selectDashboardFinishedCasesFilter,
    setFilter: dashboardActions.setFinishedCasesFilter,
  },
  {
    name: 'Out of treatment',
    iconName: 'OutOfTreatment',
    thunk: dashboardActions.getPatientCasesDashboardOutOfTreatment,
    selectCases: selectDashboardOutOfTreatmentCases,
    selectInfo: selectDashboardOutOfTreatmentCasesInfo,
    selectFilter: selectDashboardOutOfTreatmentCasesFilter,
    setFilter: dashboardActions.setOutOfTreatmentCasesFilter,
  },
];

export const Dashboard = () => {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [dropDownFilterIsOpen, setDropDownFilterIsOpen] = useState(false);
  const showFiltersButtonRef = useRef<HTMLDivElement | null>() as React.MutableRefObject<HTMLDivElement>;

  const { dashboardFilter: dashboardFilterModalIsShown } = useSelector(selectAppModals);

  const dropdownFilterRef = useOutsideClick<HTMLDivElement>((event) => {
    !showFiltersButtonRef?.current?.contains(event?.target as HTMLElement) && setDropDownFilterIsOpen(false);
  });

  const currentUser = useSelector(selectCurrentUser);

  const { isPhone } = useDevice();

  const onFilterClickHandler = () => {
    isPhone
      ? dispatch(appActions.setIsModalOpened({ name: 'dashboardFilter', isOpened: true }))
      : setDropDownFilterIsOpen((isShown) => !isShown);
  };

  const onCloseFilter = () => {
    dispatch(appActions.setIsModalOpened({ name: 'dashboardFilter', isOpened: false }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <Text type="h4" className={styles.title}>
            Actions Required
          </Text>
          <div className={styles.filterControls}>
            <div className={styles.filterButton} onClick={onFilterClickHandler} ref={showFiltersButtonRef}>
              <Icon name="Settings" />
              <Text type="subtitle2">Filter</Text>
            </div>
            {dropDownFilterIsOpen && !isPhone && (
              <div className={styles.filterList} ref={dropdownFilterRef}>
                <FilterList onClose={() => setDropDownFilterIsOpen(false)} />
              </div>
            )}
          </div>
        </div>
        <FilterChips filterSelector={selectDashboardFilter} actionCreator={dashboardActions.setDashboardFilter} />
      </div>
      <div className={styles.content}>
        {blocks.map((block) => (
          <DashboardMobileItem
            key={block.name}
            name={block.name}
            iconName={block.iconName}
            thunk={block.thunk}
            selectCases={block.selectCases}
            selectInfo={block.selectInfo}
            selectFilter={block.selectFilter}
            setFilter={block.setFilter}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            currentUser={currentUser}
          />
        ))}
      </div>
      {dashboardFilterModalIsShown && isPhone && (
        <FiltersListModal onClose={onCloseFilter}>
          <FilterList />
        </FiltersListModal>
      )}
    </div>
  );
};
